import { useMutation } from '@apollo/react-hooks'
import { Box, Grid, Typography } from '@material-ui/core'
import { FastField, FieldArray, Form, Formik } from 'formik'
import React from 'react'
import { default as workExperienceMutation } from '../../../apolloClient/mutations/createOrUpdateWorkExperiences'
import deleteWorkExperienceMutation from '../../../apolloClient/mutations/deleteWorkExperience'
import WORK_EXPERIENCES_QUERY from '../../../apolloClient/queries/getWorkExperiences'
import { useAuth0 } from '../../../contexts/Auth0Context'
import { useOnboarding } from '../../../contexts/OnboardingContext'
import formatDateForDB from '../../../shared/helpers/formatDateForDB'
import FormActions from '../../../_global/FormActions'
import TrixEditor from '../../../_global/TrixEditor'
import ValidatedCheckbox from '../../../_global/ValidatedCheckbox'
import ValidatedDatePicker from '../../../_global/ValidatedDatePicker'
import ValidatedTextField from '../../../_global/ValidatedTextField'
import ActionButtons from '../_local/ActionButtons'
import { validationSchema } from './validationSchema'

type WorkExperienceProps = {
  initialValues?: any
  profileReviewDialog?: boolean
}

const WorkExperienceForm = (props: WorkExperienceProps) => {
  const { logout } = useAuth0()
  const { currentExpert, back, step, next: nextStep, formRef } = useOnboarding()
  const [createOrUpdateWorkExperiences] = workExperienceMutation()
  const [deleteWorkExperience] = useMutation(deleteWorkExperienceMutation)

  const { initialValues, profileReviewDialog } = props

  const handleSubmit = async (values) => {
    const workExperiences = values.workExperiences.map(({ __typename, ...workExperience }) => {
      return {
        id: workExperience.id,
        description: workExperience.description,
        employer: workExperience.employer,
        endDate:
          workExperience.isCurrentEmployer || !workExperience.endDate ? null : formatDateForDB(workExperience.endDate),
        isCurrentEmployer: workExperience.isCurrentEmployer,
        startDate: !workExperience.startDate ? null : formatDateForDB(workExperience.startDate),
        title: workExperience.title,
      }
    })

    await createOrUpdateWorkExperiences({
      variables: {
        expertId: currentExpert.id,
        input: workExperiences,
      },
      refetchQueries: [{ query: WORK_EXPERIENCES_QUERY, variables: { id: currentExpert.id } }],
    }).then((result) => {
      if (values.isSaveAndExit) {
        logout()
      } else {
        if (step !== 7) {
          nextStep()
        }
      }
    })
  }

  return (
    <Formik
      validateOnMount={true}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
    >
      {({ isSubmitting, isValid, setFieldValue, values }) => {
        return (
          <Form autoComplete="off">
            <FieldArray name="workExperiences">
              {(arrayHelpers) => (
                <div>
                  {values.workExperiences.map((workExperience, index) => {
                    if (workExperience.endDate === 'Invalid date') {
                      workExperience.endDate = null
                    }
                    return (
                      <Box key={index}>
                        <Box mb={2}>
                          <Typography variant="body2" gutterBottom>
                            General information
                          </Typography>
                          <ValidatedTextField
                            name={`workExperiences.${index}.title`}
                            label="Title"
                            style={{ width: '75%' }}
                          />

                          <ValidatedTextField
                            name={`workExperiences.${index}.employer`}
                            label="Company"
                            style={{ width: '60%' }}
                          />
                          <Box mt={2} mb={1}>
                            <Grid container justify="space-evenly" spacing={2}>
                              <Grid item xs={12} md={4}>
                                <ValidatedDatePicker
                                  disableFuture={true}
                                  label="Start Date"
                                  name={`workExperiences.${index}.startDate`}
                                  setFieldValue={setFieldValue}
                                  value={workExperience.startDate}
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <ValidatedDatePicker
                                  disabled={workExperience.isCurrentEmployer}
                                  disableFuture={true}
                                  label="End Date"
                                  name={`workExperiences.${index}.endDate`}
                                  setFieldValue={setFieldValue}
                                  value={workExperience.endDate}
                                />
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <FastField
                                  as={ValidatedCheckbox}
                                  name={`workExperiences.${index}.isCurrentEmployer`}
                                  label="This is my current role"
                                  onClick={() => setFieldValue(`workExperiences.${index}.endDate`, null)}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>

                        <Box mb={3}>
                          <Typography variant="body2" gutterBottom>
                            Role Accomplishments
                          </Typography>
                          <FastField
                            as={TrixEditor}
                            name={`workExperiences.${index}.description`}
                            value={workExperience.description}
                            onChange={setFieldValue}
                            index={index}
                          />
                        </Box>

                        {!profileReviewDialog && (
                          <Box my={3}>
                            <ActionButtons
                              arrayHelpers={arrayHelpers}
                              showButton={values.workExperiences.length > 1}
                              index={index}
                              initialValues={initialValues}
                              primaryText="Add another role"
                              secondaryText="Remove role"
                              deleteRecordMutation={deleteWorkExperience}
                              refetchQuery={WORK_EXPERIENCES_QUERY}
                              values={values.workExperiences}
                              expertId={currentExpert.id}
                            />
                          </Box>
                        )}
                      </Box>
                    )
                  })}
                </div>
              )}
            </FieldArray>
            {step !== 7 && <FormActions step={step} back={back} isValid={isValid} isSubmitting={isSubmitting} />}
          </Form>
        )
      }}
    </Formik>
  )
}

export default React.memo(WorkExperienceForm)
